import React, { FC, useCallback, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Stack } from '@chakra-ui/react';
import { map } from 'lodash';
import round from 'lodash/round';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  DataTypeTag,
  formatTooltip,
  renderSeverityBar,
  WithResourceIcon,
} from 'components/DataDisplay';
import { renderDataTypeTag } from 'components/DataDisplay/DataType/render';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { IconTypes } from 'components/index';
import { DataTypesBySourceSubComponent } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Data/Components/DataTypesBySourceSubComponent';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { CustomTooltip } from 'containers/Compliance/Components/ComplianceDashboard/styles';
import { useDataTypeClassifiers } from 'containers/Visibility/Data/Components/hooks/useDataTypeClassifiers';
import { renderDataTypeLabels } from 'containers/Visibility/Data/Components/hooks/utils';

import { getFoundInLabel } from './utils';

interface Props {
  type: 'Data' | 'Document';
}

export const DataTypesBySource: FC<Props> = props => {
  const { type } = props;
  const dispatch = useDispatch();
  const { resourceUuid, data, resourceType } = useSelector(
    selectActiveResource,
  );
  const { dataTypesBySource, documentTypesBySource } = data;
  const { classifiers } = useDataTypeClassifiers();

  const getDataTypes = useCallback(() => {
    type === 'Data'
      ? dispatch(
          actions.getDataTypesBySource({
            q: { sourceUuid: resourceUuid },
          }),
        )
      : dispatch(
          actions.getDocumentTypesBySource({
            q: { id: resourceUuid },
          }),
        );
  }, [dispatch, resourceUuid, type]);

  useEffect(() => {
    getDataTypes();
  }, [getDataTypes]);

  const columns: TableColumnProps[] = [
    {
      header: (
        <Box pl={10}>{type === 'Data' ? 'Data Type' : 'Document Type'}</Box>
      ),
      accessor: 'data_type',
      render: ({ row }) => {
        const value = row?.data_type || row?.type_name || '';
        return (
          <WithResourceIcon
            resourceType={
              type === 'Data' ? row?.data_type : IconTypes.Documents
            }
          >
            {value}
          </WithResourceIcon>
        );
      },
    },
    ...(type === 'Data'
      ? [
          {
            header: 'Sensitivity',
            accessor: 'sensitivity_category',
            render: ({ row }) =>
              renderDataTypeTag(
                row?.sensitivity_category?.toUpperCase() || '-',
                {
                  showIcon: false,
                  color:
                    customTheme.colors?.[
                      row?.sensitivity_level?.toLowerCase()
                    ] ?? customTheme.colors.gray['300'],
                },
              ),
            align: 'left',
          },
          {
            header: 'Geo',
            accessor: 'geo',
            align: 'left',
          },
        ]
      : [
          {
            header: 'Sub-Types',
            accessor: 'subtypes',
          },
        ]),

    {
      header: 'Found In',
      render: ({ row }) => {
        const amount = type === 'Data' ? row?.found_in_amount : 1;
        return getFoundInLabel(resourceType, amount);
      },
      align: 'left',
    },
    ...(resourceType === NativeResources.GitHubRepository ||
    resourceType === NativeResources.BitBucketRepository ||
    resourceType === NativeResources.GitLabProject
      ? [
          {
            header: 'Branch',
            render: ({ row }) => {
              return (
                <CustomTooltip
                  label={formatTooltip({
                    header: 'Branches',
                    content: (
                      <Stack spacing={1}>
                        {map(row?.branches, o => (
                          <Box key={o}>{o}</Box>
                        ))}
                      </Stack>
                    ),
                  })}
                >
                  {row?.branches?.length || 0}
                </CustomTooltip>
              );
            },
            align: 'left',
          },
        ]
      : []),
    {
      header: 'Count',
      render: ({ row }) => {
        const count = row?.count;
        const rowCount = row?.row_count;
        const displayedCount =
          rowCount != null && count != null ? count * rowCount : count;

        return (
          formatNumber(displayedCount) +
          ' (' +
          round(row?.percent || row?.percentage || 0) +
          '%)'
        );
      },
      align: 'left',
    },
    {
      header: 'Label(s)',
      render: renderDataTypeLabels,
      align: 'left',
    },
  ];

  return (
    <Box w="full" minH="400px" flexShrink={0}>
      <Table
        columns={columns}
        data={
          type === 'Document'
            ? documentTypesBySource.data
            : dataTypesBySource.data
        }
        isLoading={
          dataTypesBySource.isLoading || documentTypesBySource.isLoading
        }
        subComponent={({ row, expanded }) =>
          expanded && (
            <DataTypesBySourceSubComponent
              type={type}
              row={row}
              isExpanded={expanded}
            />
          )
        }
        {...(type === 'Document'
          ? {
              pagination: {
                totalCount: documentTypesBySource.page.totalCount,
                pageInfo: documentTypesBySource.page.info,
                onChange: pageInfo =>
                  dispatch(
                    actions.updateDocumentTypesBySourcePagination(pageInfo),
                  ),
              },
            }
          : {})}
        cursor="pointer"
      />
    </Box>
  );
};
